/**
 * The connection dialog's footer (moved out of `NewConnectionPopup`, C0). Two groups in a wrapping row: [Test
 * connection, Delete (edit mode)] and [Cancel, Save / Update]; only the button whose run is in flight relabels to
 * "Testing…", and Cancel is never gated on a test — a stuck test must never freeze the dialog shut. The start view has
 * a Cancel-only footer (D17). In the short tier (C1, plan §3.5) the buttons are `xs` (28 px) in `px-4 py-2` rows with an
 * 8 px row gap: one row is 45 px, two rows 81 px.
 */
import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import type { TestSource } from '../hooks/useConnectionTestRun';

type Props = {
  /** The start view: Cancel only. */
  startView?: boolean;
  isEditMode: boolean;
  short: boolean;
  testing: TestSource | null;
  deleting: boolean;
  onTest: () => void;
  onDelete?: () => void;
  onCancel: () => void;
};

const PRESS = 'flex items-center gap-2 active:scale-[0.96] transition-[color,box-shadow,scale]';

function Testing() {
  return (
    <>
      <RandomDotmSpinner size={16} />
      Testing…
    </>
  );
}

export function ConnectionDialogFooter({ startView = false, isEditMode, short, testing, deleting, onTest, onDelete, onCancel }: Props) {
  const busy = testing !== null || deleting;
  const size = short ? 'xs' : 'default';
  return (
    <div
      data-dialog-fixed
      // The start view's footer is part of its page, and fades in with its chrome when the view comes back (C1b).
      data-flip-page={startView ? '' : undefined}
      data-flip-chrome={startView ? 'fade' : undefined}
      className={cn('flex shrink-0 flex-wrap items-center justify-between gap-x-2 gap-y-2 border-t', short ? 'px-4 py-2' : 'px-6 py-3')}
      data-testid="connection-dialog-footer"
    >
      {!startView && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size={size} onClick={onTest} disabled={busy} className={PRESS} data-testid="test-connection-button">
            {testing === 'test' ? <Testing /> : 'Test connection'}
          </Button>
          {isEditMode && onDelete && (
            // Outline, not filled: Delete must not draw the eye before Update (final UX review UX-18).
            <Button
              type="button"
              variant="outline"
              size={size}
              onClick={onDelete}
              disabled={busy}
              className={cn(PRESS, 'border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive')}
              data-testid="delete-connection-button"
            >
              {deleting ? <RandomDotmSpinner size={16} /> : <Trash2 className="h-4 w-4" />}
              Delete
            </Button>
          )}
        </div>
      )}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size={size} onClick={onCancel} disabled={deleting} data-testid="cancel-button">
          Cancel
        </Button>
        {!startView && (
          <Button type="submit" form="connection-form" size={size} disabled={busy} className={PRESS} data-testid="save-connection-button">
            {testing === 'submit' ? <Testing /> : isEditMode ? 'Update' : 'Save'}
          </Button>
        )}
      </div>
    </div>
  );
}
